import { PageHeader } from "@/components/shared/PageHeader";
import { UserPickerMultiSelect } from "@/components/shared/UserPicker";
import { PortalPopover } from "@/components/ui/PortalPopover";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCaseErrors } from "@/hooks/cases/useCaseErrors";
import { useChronologyRun } from "@/hooks/cases/useChronologyRun";
import { useFocusSearchOnCmdF } from "@/hooks/ui/useFocusSearchOnCmdF";
import { useCaseAnalysts } from "@/hooks/useCaseAnalysts";
import {
  invalidateCasesCache,
  removeCaseFromCache,
  updateCaseInCache,
  useCases,
} from "@/hooks/useCases";
import { isSessionExpiredError } from "@/services/apiService";
import { isConflictError } from "@/utils/errorUtils";
import lscache from "lscache";
import {
  Check,
  ChevronDown,
  ChevronUp,
  CircleAlert,
  FileText,
  Files,
  Plus,
  RefreshCw,
  Search,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";

import {
  CASE_STATUS,
  DEFAULT_VALUES,
  FIELD_TYPES,
  LOCALSTORAGE_KEYS,
  SORT_DIRECTIONS,
  STATUS_COLORS,
  STATUS_NAMES,
  statusHelpers,
} from "../../constants";
import { useAuth } from "../../contexts/AuthContext";
import { useAnalysts } from "../../hooks/useAnalysts";
import { useCaseService } from "../../hooks/useCaseService";
import { useCreateCase } from "../../hooks/useCreateCase";
import { canViewUnresolvedCaseErrors } from "../../permissions/cases";
import { canAssignQC } from "../../permissions/firms";
import { useCanCreateCase, useCanRequestChronologyRun } from "../../permissions/v2/cases";
import { formatDate, getErrorMessage, isAdmin, isProfessional, sortByConfig } from "../../utils";
import DeleteCaseModal from "../DeleteCaseModal";
import { MedChronExportMenu } from "../MedicalChronology/MedChronExportMenu";
import { CaseFilterMenu } from "./CaseFilterMenu";
import QcReviewerBadges from "./QcReviewerBadges";
import { StatusColumnHelp } from "./StatusColumnHelp";
import {
  ACTIONS_LAYOUT,
  ACTION_BUTTON_CLASSES,
  ACTION_DESTRUCTIVE_CLASSES,
  ACTION_DISABLED_CLASSES,
  ACTION_ICON_STROKE_WIDTH,
  ACTION_IDLE_CLASSES,
  ACTION_SLOT_CLASSES,
  ACTION_TOOLTIP_CLASSES,
  ANCHOR_RESET_CLASSES,
  CASE_ROW_LINK_CLASSES,
  COLUMN_WIDTH,
  FILTERS_VISIBLE_TO_PROFESSIONAL,
  TABLE_SCROLLBAR_CLASSES,
  filterTypes,
  pinnedEdgeClasses,
} from "./constants";
import { buildCaseSearchIndex, searchCases } from "./utils/caseSearch";

// PortalPopover, not the absolute tooltips used elsewhere here — this cell sits inside
// the scrolling pane, which would clip one.
const UnresolvedErrorsBadge = ({ count, onClick }) => {
  return (
    <PortalPopover
      variant="dense"
      panelHalfWidthPx={104}
      panelClassName="w-52 max-w-[min(13rem,calc(100vw-1.5rem))]"
      title="Unresolved errors"
      content={
        <p className="text-sm leading-relaxed text-muted-foreground">
          {count} unresolved {count === 1 ? "error" : "errors"} — click for details
        </p>
      }
    >
      <button
        type="button"
        aria-label={`${count} unresolved ${count === 1 ? "error" : "errors"} — click for details`}
        className="mt-0.5 flex h-4 min-w-4 shrink-0 cursor-pointer items-center justify-center rounded-full bg-destructive-subtle-strong px-1 text-2xs font-semibold tabular-nums text-destructive-text transition-colors hover:scale-150"
        onClick={onClick}
      >
        !
      </button>
    </PortalPopover>
  );
};

UnresolvedErrorsBadge.propTypes = {
  count: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
};

const AIReviewBadge = () => {
  return (
    <span className="inline-flex animate-shimmer rounded-full bg-gradient-to-r from-ai/50 via-transparent to-ai/50 bg-[length:400%_100%] p-px align-middle">
      <span className="inline-flex items-center rounded-full bg-ai-subtle px-2.5 py-0.5 text-xs font-normal text-ai-text">
        <Sparkles className="mr-1 h-3 w-3" />
        <span>AI review</span>
      </span>
    </span>
  );
};

AIReviewBadge.propTypes = {};

const StatusBadge = ({ status_id, onStatusChange, canChangeStatus }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { hasPermission } = useAuth();

  // Don't allow status changes during AI Review unless super admin
  const isEditable =
    canChangeStatus && (status_id !== CASE_STATUS.AI_REVIEW || hasPermission("super_admin"));

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleStatusClick = (e) => {
    if (!isEditable) return;
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleStatusChange = (newStatus, e) => {
    e.stopPropagation();
    setIsOpen(false);
    onStatusChange(newStatus);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {status_id === CASE_STATUS.AI_REVIEW ? (
        <div
          className={`${isEditable ? "cursor-pointer hover:opacity-80" : ""}`}
          onClick={handleStatusClick}
        >
          <AIReviewBadge />
        </div>
      ) : (
        <div
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-normal ${STATUS_COLORS[status_id] || "bg-surface-sunken"} ${isEditable ? "cursor-pointer hover:opacity-80" : ""}`}
          onClick={handleStatusClick}
        >
          <span>{STATUS_NAMES[status_id] || "Unknown"}</span>
          {isEditable && <ChevronDown className="ml-1 h-3 w-3" />}
        </div>
      )}

      {isOpen && isEditable && (
        <div
          className="fixed z-popover mt-1 w-40 rounded-md bg-card py-1 shadow-lg ring-1 ring-black ring-opacity-5"
          style={{
            top: dropdownRef.current?.getBoundingClientRect().bottom + 4,
            left: dropdownRef.current?.getBoundingClientRect().left,
          }}
        >
          <div className="py-1" role="menu">
            {Object.entries(STATUS_NAMES)
              .filter(([id]) => {
                const numId = Number(id);
                // Only super admins can see AI Review in dropdown
                if (numId === 1 && !hasPermission("super_admin")) return false;
                // Only users with specific permissions can move to certain statuses
                if (numId === 4 && !hasPermission("case_actions", "finalize")) return false;
                return true;
              })
              .map(([id, name]) => (
                <button
                  key={id}
                  className={`flex w-full items-center space-x-2 px-3 py-2 text-sm hover:bg-surface-subtle ${Number(id) === status_id ? "bg-surface-subtle" : ""}`}
                  onClick={(e) => handleStatusChange(Number(id), e)}
                >
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-normal ${STATUS_COLORS[id]}`}
                  >
                    {name}
                  </span>
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

StatusBadge.propTypes = {
  status_id: PropTypes.number.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  canChangeStatus: PropTypes.bool.isRequired,
};

const CaseAnalystsCell = ({ caseId, analysts, availableAnalysts, isEditable }) => {
  const { addAnalyst, removeAnalyst } = useCaseAnalysts(caseId);

  return (
    <UserPickerMultiSelect
      users={availableAnalysts}
      value={analysts}
      onAdd={async (userId) => {
        const analyst = availableAnalysts.find((a) => a.user_id === userId);
        if (!analyst) throw new Error(`Analyst ${userId} is not in the candidate list`);
        await addAnalyst({ user_id: analyst.user_id, name: analyst.name });
      }}
      onRemove={async (userId) => {
        await removeAnalyst(userId);
      }}
      variant="blue"
      addLabel="Add Analyst"
      emptyText="No analysts available to add."
      isEditable={isEditable}
    />
  );
};

CaseAnalystsCell.propTypes = {
  caseId: PropTypes.string.isRequired,
  analysts: PropTypes.array.isRequired,
  availableAnalysts: PropTypes.arrayOf(
    PropTypes.shape({
      user_id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      role_name: PropTypes.string.isRequired,
    })
  ).isRequired,
  isEditable: PropTypes.bool.isRequired,
};

const PreviewCaseModal = ({ isOpen, onClose, onConfirm }) => {
  return (
    <Dialog open={isOpen} onOpenChange={(next) => !next && onClose()}>
      <DialogContent size="sm">
        <DialogTitle className="text-lg font-medium">Preview Chronology</DialogTitle>
        <DialogDescription className="text-muted-foreground">
          This chronology has yet to be fully reviewed by our team, so we cannot guarantee 100%
          accuracy. Would you like to preview the chronology?
        </DialogDescription>
        <div className="flex justify-end space-x-4">
          <button
            className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="rounded-md bg-primary px-4 py-2 text-sm text-white hover:bg-primary-hover"
            onClick={onConfirm}
          >
            Preview
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

PreviewCaseModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

const CaseManagement = () => {
  // const [statusTabs, setStatusTabs] = useState([
  //   { name: "Active", value: "active", count: 0 },
  //   { name: "Finished", value: "finished", count: 0 },
  //   { name: "Archived", value: "staged", count: 0 },
  // ]);
  const [isCreatingCase, setIsCreatingCase] = useState(false);
  // const [selectedStatus, setSelectedStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  // const [selectedCases, setSelectedCases] = useState([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewCaseId, setPreviewCaseId] = useState(null);
  const navigate = useNavigate();
  const [activeFilters, setActiveFilters] = useState({});
  const { hasPermission, firmContext, isMyCases } = useAuth();
  const canCreateCase = useCanCreateCase(firmContext?.firmId);
  const canRequestChronologyRun = useCanRequestChronologyRun(firmContext?.firmId);
  const [sortConfig, setSortConfig] = useState(() => {
    const savedConfig = lscache.get(LOCALSTORAGE_KEYS.CASE_MANAGEMENT_SORT_CONFIG);
    return (
      savedConfig || {
        field: null,
        direction: DEFAULT_VALUES.SORT_DIRECTION,
      }
    );
  });
  const [showDeleteCaseModal, setShowDeleteCaseModal] = useState(false);
  const [caseToDelete, setCaseToDelete] = useState(null);
  const { analysts } = useAnalysts();
  const { createCase } = useCreateCase();
  const caseService = useCaseService();
  const { rerunChronology } = useChronologyRun();
  const { resolveAllCaseErrors } = useCaseErrors();
  // const [error, setError] = useState(null);
  const canViewUnresolvedErrors = canViewUnresolvedCaseErrors(firmContext);

  const {
    cases,
    isLoading: loading,
    error: casesError,
    errorMessage: casesErrorMessage,
  } = useCases();

  useEffect(() => {
    if (!casesError || isSessionExpiredError(casesError)) return;
    // Only surface the error when the table has nothing to show — a background
    // poll failure while cases are already on screen shouldn't interrupt the user.
    if (cases.length === 0) {
      console.error("Error fetching cases:", casesErrorMessage);
      toast.error("Failed to load cases. Please try refreshing the page.", {
        id: "cases-load-error",
      });
    }
  }, [casesError, casesErrorMessage, cases.length]);

  // Add state for error modal
  const [selectedErrors, setSelectedErrors] = useState(null);
  const [isTableScrolled, setIsTableScrolled] = useState(false);

  // Tracks which case rows have an export in flight, fed by each row's MedChronExportMenu
  // via onExportingChange — used to gate the Rerun/Delete row actions, not the export itself.
  const [exportingCaseIds, setExportingCaseIds] = useState(() => new Set());

  const handleExportingChange = useCallback((caseId, isCaseExporting) => {
    setExportingCaseIds((prev) => {
      // Bail out when membership is unchanged: each row passes a fresh onExportingChange
      // callback every render, so its effect re-fires every render. Returning a new Set
      // unconditionally would re-render this component each time and loop indefinitely.
      if (isCaseExporting === prev.has(caseId)) return prev;
      const next = new Set(prev);
      if (isCaseExporting) {
        next.add(caseId);
      } else {
        next.delete(caseId);
      }
      return next;
    });
  }, []);

  // Define field types for sorting
  const caseFieldTypes = {
    exam_date: FIELD_TYPES.DATE,
    due_date: FIELD_TYPES.DATE,
    created_at: FIELD_TYPES.DATE,
    files: FIELD_TYPES.NUMBER,
    pages: FIELD_TYPES.NUMBER,
    analysts: FIELD_TYPES.ARRAY_LENGTH,
    qc_reviewers: FIELD_TYPES.ARRAY_LENGTH,
  };

  const updateSortConfig = (field) => {
    setSortConfig((prevConfig) => {
      let newDirection = SORT_DIRECTIONS.ASC;

      if (prevConfig.field === field) {
        newDirection =
          prevConfig.direction === SORT_DIRECTIONS.ASC ? SORT_DIRECTIONS.DESC : SORT_DIRECTIONS.ASC;
      }

      const newConfig = {
        field,
        direction: newDirection,
      };

      lscache.set(LOCALSTORAGE_KEYS.CASE_MANAGEMENT_SORT_CONFIG, newConfig);

      return newConfig;
    });
  };

  const getSortedCases = (cases) => {
    if (!sortConfig?.field) {
      return cases;
    }
    return sortByConfig(cases, sortConfig, caseFieldTypes);
  };

  // const isActiveStatus = (status_id) => {
  //   // Error (-1) is not considered active, and active statuses are 0-4
  //   return status_id >= 0 && status_id <= 4;
  // };

  const getTabCounts = () => {
    // const counts = {
    //   active: cases.filter((c) => isActiveStatus(c.status_id)).length,
    //   finished: cases.filter((c) => c.status_id === 4).length,
    //   staged: cases.filter((c) => c.status_id === 6).length,
    // };
    // setStatusTabs((prevTabs) =>
    //   prevTabs.map((tab) => ({
    //     ...tab,
    //     count: counts[tab.value] || 0,
    //   }))
    // );
  };

  useEffect(() => {
    if (cases.length > 0) {
      getTabCounts(cases);
    }
  }, [cases]);

  const filterOptionsByType = useMemo(() => {
    const getFilterOptions = (filterType) => {
      if (!cases.length) return [];

      const field = filterTypes[filterType].field;

      if (filterType === "status") {
        return Object.keys(STATUS_NAMES).map((id) => STATUS_NAMES[id]);
      }

      if (filterType === "analyst") {
        return [
          ...new Set(
            cases
              .map((c) => c.analysts?.map?.((x) => x.name))
              .flat()
              .filter(Boolean)
          ),
        ].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
      }

      return [...new Set(cases.map((c) => c[field]).filter(Boolean))];
    };

    return Object.fromEntries(
      Object.keys(filterTypes).map((filterType) => [filterType, getFilterOptions(filterType)])
    );
  }, [cases]);

  const getFilteredAndSearchedCases = () => {
    let filtered = [...cases];

    // Apply all active filters
    Object.entries(activeFilters).forEach(([filterType, selectedValues]) => {
      if (selectedValues.length > 0) {
        filtered = filtered.filter((item) => {
          const field = filterTypes[filterType].field;
          if (filterType === "status") {
            const statusName = STATUS_NAMES[item.status_id];
            return selectedValues.includes(statusName);
          }
          if (filterType === "analyst") {
            return item?.analysts?.some?.((x) => selectedValues.includes(x.name)) ?? false;
          }
          return selectedValues.includes(item[field]);
        });
      }
    });

    // Apply search query
    if (searchQuery) {
      const results = searchCases(caseSearchIndex, searchQuery);
      const matchedIds = new Set(results.map((r) => r.id));
      filtered = filtered.filter((item) => matchedIds.has(item.case_id));
    }

    return getSortedCases(filtered);
  };

  const shouldPreviewChronology = (caseItem) =>
    isProfessional(firmContext) && statusHelpers.shouldShowPreviewModal(caseItem.status_id);

  // Shared by the row fallback and the chronology links so the files/preview/navigate
  // decision can't drift between a cell's href and what clicking it actually does.
  const shouldPreviewCaseRow = (caseItem) =>
    !statusHelpers.shouldNavigateToFiles(caseItem.status_id) && shouldPreviewChronology(caseItem);

  const openChronologyPreview = (caseId) => {
    setPreviewCaseId(caseId);
    setShowPreviewModal(true);
  };

  const getCaseRowPath = (caseItem) =>
    statusHelpers.shouldNavigateToFiles(caseItem.status_id)
      ? `/cases/${caseItem.case_id}/files`
      : `/cases/${caseItem.case_id}/chronology`;

  // Fallback for row clicks that land outside a link (padding, interactive cells).
  const handleRowClick = (caseId) => {
    const caseItem = cases.find((c) => c.case_id === caseId);
    if (!caseItem) return;

    if (shouldPreviewCaseRow(caseItem)) {
      openChronologyPreview(caseItem.case_id);
    } else {
      navigate(getCaseRowPath(caseItem));
    }
  };

  // Stops propagation so the row fallback never re-navigates — needed both for a
  // modifier-click (browser handles it) and a plain click on a non-previewable row
  // (native link navigation already fired).
  const handleChronologyLinkClick = (caseItem, e) => {
    e.stopPropagation();
    const isPlainLeftClick = e.button === 0 && !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey);
    if (isPlainLeftClick && shouldPreviewCaseRow(caseItem)) {
      e.preventDefault();
      openChronologyPreview(caseItem.case_id);
    }
  };

  const handleCaseRowLinkClick = (caseItem, e) => {
    if (statusHelpers.shouldNavigateToFiles(caseItem.status_id)) {
      e.stopPropagation();
      return;
    }
    handleChronologyLinkClick(caseItem, e);
  };

  // Hidden from the tab order and the accessibility tree — these only widen the click
  // target; the case-name link and the row's action icons stay reachable.
  const caseRowCellLinkProps = (caseItem) => ({
    to: getCaseRowPath(caseItem),
    onClick: (e) => handleCaseRowLinkClick(caseItem, e),
    tabIndex: -1,
    "aria-hidden": true,
  });

  const handleDeleteClick = (case_id) => {
    setCaseToDelete(case_id);
    setShowDeleteCaseModal(true);
  };

  const confirmDeleteCase = async () => {
    try {
      await caseService.deleteCase(caseToDelete);
      removeCaseFromCache(caseToDelete);
      setShowDeleteCaseModal(false);
      setCaseToDelete(null);
    } catch (error) {
      console.error("Error deleting case:", error);
      // You might want to show an error message to the user here
    }
  };

  const handleFilterValueToggle = (filterType, value) => {
    setActiveFilters((prev) => {
      const currentValues = prev[filterType] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];

      return { ...prev, [filterType]: newValues };
    });
  };

  const removeFilter = (filterType, value) => {
    setActiveFilters((prev) => ({
      ...prev,
      [filterType]: prev[filterType].filter((v) => v !== value),
    }));
  };

  const clearAllFilters = () => {
    setActiveFilters({});
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const canDeleteCase = () => {
    return hasPermission("case_actions", "delete") || hasPermission("super_admin");
  };

  const canChangeStatus = () => {
    // Check for either update_status permission or super_admin
    return hasPermission("case_actions", "update_status") || hasPermission("super_admin");
  };

  const handleStatusChange = async (caseId, newStatus) => {
    try {
      // Check if case is in AI Review and user is not super admin
      const caseItem = cases.find((c) => c.case_id === caseId);
      if (caseItem?.status_id === CASE_STATUS.AI_REVIEW && !hasPermission("super_admin")) {
        toast.error("Only super admins can move cases out of AI review");
        return;
      }

      // Additional permission checks for specific statuses
      if (newStatus === CASE_STATUS.FINISHED && !hasPermission("case_actions", "finalize")) {
        toast.error("You do not have permission to finalize cases");
        return;
      }

      await caseService.updateCaseStatus(caseId, newStatus);
      updateCaseInCache(caseId, { status_id: newStatus });
    } catch (error) {
      console.error("Error updating case status:", error);
      toast.error("Failed to update case status");
    }
  };

  // Pinned headers outrank plain ones so the body's own pinned cells scroll under them.
  const SortableHeader = ({
    field,
    label,
    width = "w-48",
    align = "center",
    pinnedLeft = null,
  }) => {
    const isSorted = sortConfig.field === field;
    const Arrow = isSorted && sortConfig.direction === "desc" ? ChevronDown : ChevronUp;
    const isPinned = pinnedLeft !== null;

    return (
      <th
        scope="col"
        style={isPinned ? { left: pinnedLeft } : undefined}
        className={`${width} group sticky top-0 cursor-pointer select-none whitespace-nowrap bg-surface-subtle px-4 py-2.5 text-2xs font-semibold uppercase tracking-eyebrow transition-colors hover:text-foreground ${
          isSorted ? "text-foreground" : "text-muted-foreground"
        } ${align === "left" ? "text-left" : align === "right" ? "text-right" : "text-center"} ${
          isPinned ? `z-30 ${pinnedEdgeClasses(isTableScrolled)}` : "z-20"
        }`}
        onClick={() => updateSortConfig(field)}
        aria-sort={
          isSorted ? (sortConfig.direction === "asc" ? "ascending" : "descending") : "none"
        }
      >
        <div
          className={`flex items-center gap-1 ${align === "left" ? "justify-start" : align === "right" ? "justify-end" : "justify-center"}`}
        >
          <span>{label}</span>
          <Arrow
            className={`h-3.5 w-3.5 shrink-0 transition-opacity ${
              isSorted ? "text-primary opacity-100" : "opacity-0 group-hover:opacity-60"
            }`}
          />
        </div>
      </th>
    );
  };

  SortableHeader.propTypes = {
    field: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    width: PropTypes.string,
    align: PropTypes.oneOf(["left", "center", "right"]),
    pinnedLeft: PropTypes.number,
  };

  const tableScrollRef = useRef(null);
  const tableFadeRef = useRef(null);
  const searchInputRef = useRef(null);
  const contentRef = useRef(null);
  useEffect(() => {
    // Layout.jsx renders ".content" on desktop, ".mobile-content" on mobile — only one exists.
    contentRef.current = document.querySelector(".content, .mobile-content");
  }, []);
  const isAnyModalOpen = showPreviewModal || showDeleteCaseModal || Boolean(selectedErrors);
  useFocusSearchOnCmdF(searchInputRef, contentRef, { disabled: isAnyModalOpen });

  // const handlePhysicianChange = async (caseId, newPhysicianId) => {
  //   try {
  //     const response = await api.put(`/cases/${caseId}/physician`, {
  //       user_id: newPhysicianId,
  //     });
  //     return response.data;
  //   } catch (error) {
  //     console.error("Error updating physician:", error);
  //     throw error;
  //   }
  // };

  // const handleAnalystChange = async (caseId, analystId) => {
  //   try {
  //     const response = await api.put(`/cases/${caseId}/analyst`, {
  //       user_id: analystId,
  //     });
  //     return response.data;
  //   } catch (error) {
  //     console.error("Error updating analyst:", error);
  //     throw error;
  //   }
  // };

  // Add error boundary for component
  useEffect(() => {
    const handleError = (event) => {
      console.error("Unhandled error:", event.reason);
      toast.error("Something went wrong. Please try refreshing the page.");
    };

    window.addEventListener("unhandledrejection", handleError);
    return () => window.removeEventListener("unhandledrejection", handleError);
  }, []);

  const handleNewCase = async () => {
    if (isCreatingCase) return;

    setIsCreatingCase(true);
    try {
      const caseData = await createCase();
      if (!caseData) return;

      // createCase returns the raw inserted row, which lacks the joined fields the list
      // renders (analysts, qc_reviewers, file/page counts) — refetch instead of patching.
      invalidateCasesCache();
      navigate(`/cases/${caseData.case.case_id}/files`);
    } finally {
      setIsCreatingCase(false);
    }
  };

  const handlePreviewConfirm = () => {
    setShowPreviewModal(false);
    if (previewCaseId) {
      navigate(`/cases/${previewCaseId}/chronology`);
    }
    setPreviewCaseId(null);
  };

  const handleRerunChronology = async (caseId) => {
    try {
      await rerunChronology(caseId);
      toast.success("Chronology re-run started");
    } catch (error) {
      if (isSessionExpiredError(error)) {
        return; // API service will handle redirect
      }
      if (isConflictError(error)) {
        toast(getErrorMessage(error), { icon: "ℹ️" });
        return;
      }
      console.error("Error re-running chronology:", error);
      toast.error(getErrorMessage(error));
    }
  };

  useEffect(() => {
    const tableContainer = tableScrollRef.current;
    const fadeElement = tableFadeRef.current;
    if (!tableContainer || !fadeElement) return;

    const handleScrollAndResize = () => {
      const hasOverflow = tableContainer.scrollWidth > tableContainer.clientWidth;
      const isAtEnd =
        tableContainer.scrollLeft + tableContainer.clientWidth >= tableContainer.scrollWidth - 10;

      fadeElement.style.opacity = hasOverflow && !isAtEnd ? "1" : "0";
      setIsTableScrolled(tableContainer.scrollLeft > 0);
    };

    tableContainer.addEventListener("scroll", handleScrollAndResize);
    window.addEventListener("resize", handleScrollAndResize);
    handleScrollAndResize();

    return () => {
      tableContainer.removeEventListener("scroll", handleScrollAndResize);
      window.removeEventListener("resize", handleScrollAndResize);
    };
  }, [cases]); // Re-run when cases change as this might affect table width

  const canResolveErrors = () => {
    return hasPermission("super_admin");
  };

  const handleResolveAllErrors = async (caseId, e) => {
    e.stopPropagation();
    try {
      await resolveAllCaseErrors(caseId);
      toast.success("All errors resolved");
    } catch (error) {
      console.error("Error resolving errors:", error);
      toast.error("Failed to resolve errors");
    }
  };

  // Add error modal close handler
  const handleCloseErrorModal = () => {
    setSelectedErrors(null);
  };

  // Four actions always render; Delete and Resolve are permission-gated. Must stay below
  // canDeleteCase/canResolveErrors — `useMemo` runs during render, so hoisting it is a TDZ error.
  const actionsColumnCount = useMemo(
    () => 4 + (canDeleteCase() ? 1 : 0) + (canResolveErrors() ? 1 : 0),
    [firmContext]
  );

  const caseSearchIndex = useMemo(() => buildCaseSearchIndex(cases), [cases]);

  const visibleCases = getFilteredAndSearchedCases();
  const availableFilterTypes = Object.entries(filterTypes)
    .filter(
      ([key]) => !isProfessional(firmContext) || FILTERS_VISIBLE_TO_PROFESSIONAL.includes(key)
    )
    .map(([key, { label }]) => ({ key, label }));
  const visibleCaseCount = visibleCases.length;
  const showStaffColumns = !isProfessional(firmContext);

  // In My Cases the un-pinned Firm column comes first, so pinning the name past it would
  // bury Firm permanently. The rightmost frozen column carries the scroll cue.
  const isNamePinned = !isMyCases;
  const showIndexShadow = isTableScrolled && !isNamePinned;
  const showNameShadow = isTableScrolled && isNamePinned;

  const tableMinWidth =
    COLUMN_WIDTH.index +
    (isMyCases ? COLUMN_WIDTH.firm : 0) +
    COLUMN_WIDTH.caseName +
    ACTIONS_LAYOUT[actionsColumnCount].columnWidth +
    COLUMN_WIDTH.status +
    (showStaffColumns
      ? COLUMN_WIDTH.physician + COLUMN_WIDTH.analyst + COLUMN_WIDTH.qcReviewer
      : 0) +
    COLUMN_WIDTH.examDate +
    COLUMN_WIDTH.dueDate +
    COLUMN_WIDTH.files +
    COLUMN_WIDTH.pages +
    COLUMN_WIDTH.createdAt;

  // Index, case name, actions, status, and the five date/count columns are always rendered.
  const tableColumnCount = 9 + (isMyCases ? 1 : 0) + (showStaffColumns ? 3 : 0);

  return (
    <div className="min-h-full bg-surface-sunken">
      <div className="mx-auto w-full max-w-[1800px] px-8 pb-6 pt-4">
        <DeleteCaseModal
          isOpen={showDeleteCaseModal}
          onClose={() => {
            setShowDeleteCaseModal(false);
            setCaseToDelete(null);
          }}
          onConfirm={confirmDeleteCase}
        />

        <PreviewCaseModal
          isOpen={showPreviewModal}
          onClose={() => {
            setShowPreviewModal(false);
            setPreviewCaseId(null);
          }}
          onConfirm={handlePreviewConfirm}
        />

        <PageHeader
          title="Cases"
          meta={[loading ? "(loading\u2026)" : `(${visibleCaseCount} total)`]}
          metaPlacement="inline"
          actions={
            canCreateCase ? (
              <button
                onClick={handleNewCase}
                disabled={isCreatingCase}
                className="inline-flex items-center rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary-hover focus-visible:shadow-focus active:bg-primary-active disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Plus size={16} className="mr-2" /> {isCreatingCase ? "Creating\u2026" : "New Case"}
              </button>
            ) : undefined
          }
          className="mb-4"
        />

        <div className="rounded-xl border border-border bg-card shadow-sm">
          <div className="relative z-10 border-b border-border px-5 py-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <CaseFilterMenu
                    filterTypes={availableFilterTypes}
                    activeFilters={activeFilters}
                    getValueOptions={(filterType) => filterOptionsByType[filterType]}
                    onToggleValue={handleFilterValueToggle}
                    isLoading={loading}
                  />

                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2.5">
                      <Search className="h-4 w-4 text-text-faint" />
                    </div>
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search"
                      className="inline-flex h-9 w-72 items-center rounded-lg border border-border-strong bg-card px-3 pl-9 text-sm text-foreground transition-colors placeholder:text-text-faint hover:border-primary-border focus-visible:shadow-focus"
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Active filters display */}
              {Object.keys(activeFilters).length > 0 && (
                <div className="flex items-center space-x-2">
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(activeFilters).map(([filterType, values]) =>
                      values.map((value) => (
                        <span
                          key={`${filterType}-${value}`}
                          className="inline-flex items-center rounded-full bg-primary-subtle px-3 py-1 text-sm text-primary-emphasis"
                        >
                          {filterTypes[filterType].label}: {value}
                          <X
                            className="ml-2 h-4 w-4 cursor-pointer hover:text-primary"
                            onClick={() => removeFilter(filterType, value)}
                          />
                        </span>
                      ))
                    )}
                    <button
                      className="text-sm text-muted-foreground hover:text-foreground"
                      onClick={clearAllFilters}
                    >
                      Clear all
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="relative z-0 overflow-hidden rounded-b-xl">
            <div className="relative">
              <div
                ref={tableFadeRef}
                className="table-fade pointer-events-none absolute bottom-0 right-0 top-0 z-20 hidden w-20 bg-gradient-to-l from-card via-card/80 to-transparent opacity-0 transition-opacity duration-200 md:block"
              />
              <div
                ref={tableScrollRef}
                className={`relative max-h-[calc(100vh-14rem)] overflow-auto ${TABLE_SCROLLBAR_CLASSES}`}
              >
                <div style={{ minWidth: tableMinWidth }}>
                  <table className="w-full table-fixed">
                    <thead className="sticky top-0 z-20 bg-surface-subtle">
                      <tr className="border-b border-border-strong">
                        <th
                          scope="col"
                          className={`sticky left-0 top-0 z-30 w-16 bg-surface-subtle py-2.5 pl-8 pr-4 text-left text-2xs font-semibold uppercase tracking-eyebrow text-muted-foreground ${pinnedEdgeClasses(
                            showIndexShadow
                          )}`}
                        >
                          #
                        </th>
                        {isMyCases && (
                          <SortableHeader
                            field="firm_name"
                            label="Firm"
                            width="w-48"
                            align="left"
                          />
                        )}
                        <SortableHeader
                          field="case_name"
                          label="Case name"
                          width="w-56"
                          align="left"
                          pinnedLeft={isNamePinned ? COLUMN_WIDTH.index : null}
                        />
                        <th
                          scope="col"
                          className={`sticky top-0 z-20 whitespace-nowrap bg-surface-subtle px-4 py-2.5 text-center text-2xs font-semibold uppercase tracking-eyebrow text-muted-foreground ${ACTIONS_LAYOUT[actionsColumnCount].headerWidthClass}`}
                        >
                          Actions
                        </th>
                        <th
                          scope="col"
                          className="sticky top-0 z-20 w-36 bg-surface-subtle px-4 py-2.5 text-center text-2xs font-semibold uppercase tracking-eyebrow text-muted-foreground"
                        >
                          <div className="flex items-center justify-center space-x-1">
                            <span>Status</span>
                            <StatusColumnHelp />
                          </div>
                        </th>
                        {showStaffColumns && (
                          <>
                            <SortableHeader
                              field="physician"
                              label="Physician"
                              width="w-44"
                              align="left"
                            />
                            <SortableHeader field="analysts" label="Analyst" width="w-44" />
                            <SortableHeader field="qc_reviewers" label="QC Reviewer" width="w-36" />
                          </>
                        )}
                        <SortableHeader field="exam_date" label="Exam Date" width="w-28" />
                        <SortableHeader field="due_date" label="Due Date" width="w-28" />
                        <SortableHeader field="files" label="Files" width="w-16" />
                        <SortableHeader field="pages" label="Pages" width="w-16" />
                        <SortableHeader field="created_at" label="Created On" width="w-28" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-subtle bg-card">
                      {loading ? (
                        <tr>
                          <td colSpan={tableColumnCount} className="px-4 py-2.5 text-center">
                            Loading...
                          </td>
                        </tr>
                      ) : visibleCaseCount === 0 ? (
                        <tr>
                          <td
                            colSpan={tableColumnCount}
                            className="px-6 py-12 text-center text-muted-foreground"
                          >
                            No cases found. Click &quot;New Case&quot; to create a new case.
                          </td>
                        </tr>
                      ) : (
                        visibleCases.map((caseItem, index) => (
                          <tr
                            key={caseItem.case_id}
                            className="group/row cursor-pointer transition-colors hover:bg-accent"
                            onClick={() => handleRowClick(caseItem.case_id)}
                          >
                            <td
                              className={`sticky left-0 z-10 w-16 bg-card p-0 text-sm tabular-nums text-text-faint transition-colors group-hover/row:bg-accent ${pinnedEdgeClasses(
                                showIndexShadow
                              )}`}
                            >
                              <Link
                                {...caseRowCellLinkProps(caseItem)}
                                className={`${CASE_ROW_LINK_CLASSES} py-2.5 pl-8 pr-4`}
                              >
                                {index + 1}
                              </Link>
                            </td>
                            {isMyCases && (
                              <td className="min-w-0 max-w-[12rem] overflow-hidden p-0 text-left">
                                <Link
                                  {...caseRowCellLinkProps(caseItem)}
                                  className={`${CASE_ROW_LINK_CLASSES} px-4 py-2.5`}
                                >
                                  <div className="flex min-w-0 items-center break-words text-sm text-foreground">
                                    {caseItem.firm_name}
                                  </div>
                                </Link>
                              </td>
                            )}
                            <td
                              style={isNamePinned ? { left: COLUMN_WIDTH.index } : undefined}
                              className={`min-w-0 max-w-[14rem] bg-card px-4 py-2.5 text-left transition-colors group-hover/row:bg-accent ${
                                isNamePinned ? "sticky z-10" : ""
                              } ${pinnedEdgeClasses(showNameShadow)}`}
                            >
                              <div className="flex min-w-0 items-start gap-1.5">
                                <Link
                                  to={getCaseRowPath(caseItem)}
                                  onClick={(e) => handleCaseRowLinkClick(caseItem, e)}
                                  className={`min-w-0 break-words text-sm text-foreground transition-colors duration-150 group-hover/row:text-primary-emphasis ${ANCHOR_RESET_CLASSES}`}
                                >
                                  {caseItem.case_name}
                                </Link>
                                {canViewUnresolvedErrors &&
                                  caseItem.unresolved_errors?.length > 0 && (
                                    <UnresolvedErrorsBadge
                                      count={caseItem.unresolved_errors.length}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedErrors(caseItem.unresolved_errors);
                                      }}
                                    />
                                  )}
                              </div>
                            </td>
                            <td className="h-11 whitespace-nowrap px-4 py-2.5">
                              <div
                                className={`mx-auto grid h-full w-fit items-center justify-items-center gap-0.5 ${ACTIONS_LAYOUT[actionsColumnCount].gridClasses}`}
                              >
                                {/* View Chronology */}
                                <div className={ACTION_SLOT_CLASSES}>
                                  {statusHelpers.shouldShowChronIcon(caseItem.status_id) ? (
                                    <Link
                                      to={`/cases/${caseItem.case_id}/chronology`}
                                      aria-label="View Chronology"
                                      className={`${ACTION_BUTTON_CLASSES} ${ACTION_IDLE_CLASSES} ${ANCHOR_RESET_CLASSES}`}
                                      onClick={(e) => handleChronologyLinkClick(caseItem, e)}
                                    >
                                      <FileText
                                        className="h-5 w-5"
                                        strokeWidth={ACTION_ICON_STROKE_WIDTH}
                                      />
                                      <span className={ACTION_TOOLTIP_CLASSES}>
                                        View Chronology
                                      </span>
                                    </Link>
                                  ) : null}
                                </div>

                                {/* Rerun Chronology */}
                                <div className={ACTION_SLOT_CLASSES}>
                                  {canRequestChronologyRun &&
                                  statusHelpers.canRerunChronology(caseItem.status_id) ? (
                                    <button
                                      aria-label="Re-run Chronology"
                                      className={`${ACTION_BUTTON_CLASSES} ${
                                        exportingCaseIds.has(caseItem.case_id)
                                          ? ACTION_DISABLED_CLASSES
                                          : ACTION_IDLE_CLASSES
                                      }`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (!exportingCaseIds.has(caseItem.case_id)) {
                                          handleRerunChronology(caseItem.case_id);
                                        }
                                      }}
                                      disabled={exportingCaseIds.has(caseItem.case_id)}
                                    >
                                      <RefreshCw
                                        className="h-5 w-5"
                                        strokeWidth={ACTION_ICON_STROKE_WIDTH}
                                      />
                                      <span className={ACTION_TOOLTIP_CLASSES}>
                                        Re-run Chronology
                                      </span>
                                    </button>
                                  ) : null}
                                </div>

                                {/* View Files */}
                                <div className={ACTION_SLOT_CLASSES}>
                                  <Link
                                    to={`/cases/${caseItem.case_id}/files`}
                                    aria-label="View Files"
                                    className={`${ACTION_BUTTON_CLASSES} ${ACTION_IDLE_CLASSES} ${ANCHOR_RESET_CLASSES}`}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <Files
                                      className="h-5 w-5"
                                      strokeWidth={ACTION_ICON_STROKE_WIDTH}
                                    />
                                    <span className={ACTION_TOOLTIP_CLASSES}>View Files</span>
                                  </Link>
                                </div>

                                {/* Delete Case */}
                                {canDeleteCase() && (
                                  <div className={ACTION_SLOT_CLASSES}>
                                    <button
                                      aria-label="Delete Case"
                                      className={`${ACTION_BUTTON_CLASSES} ${
                                        exportingCaseIds.has(caseItem.case_id)
                                          ? ACTION_DISABLED_CLASSES
                                          : ACTION_DESTRUCTIVE_CLASSES
                                      }`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (!exportingCaseIds.has(caseItem.case_id)) {
                                          handleDeleteClick(caseItem.case_id);
                                        }
                                      }}
                                      disabled={exportingCaseIds.has(caseItem.case_id)}
                                    >
                                      <Trash2
                                        className="h-5 w-5"
                                        strokeWidth={ACTION_ICON_STROKE_WIDTH}
                                      />
                                      <span className={ACTION_TOOLTIP_CLASSES}>Delete Case</span>
                                    </button>
                                  </div>
                                )}

                                {/* Download Case */}
                                <div className={ACTION_SLOT_CLASSES}>
                                  <MedChronExportMenu
                                    variant="list"
                                    caseId={caseItem.case_id}
                                    caseName={caseItem.case_name}
                                    disabled={!statusHelpers.canDownload(caseItem.status_id)}
                                    onExportingChange={(isCaseExporting) =>
                                      handleExportingChange(caseItem.case_id, isCaseExporting)
                                    }
                                  />
                                </div>

                                {/* Resolve All Errors */}
                                {canResolveErrors() && caseItem.unresolved_errors?.length > 0 && (
                                  <div className={ACTION_SLOT_CLASSES}>
                                    <button
                                      aria-label="Resolve All Errors"
                                      className={`${ACTION_BUTTON_CLASSES} ${ACTION_IDLE_CLASSES}`}
                                      onClick={(e) => handleResolveAllErrors(caseItem.case_id, e)}
                                    >
                                      <Check
                                        className="h-5 w-5"
                                        strokeWidth={ACTION_ICON_STROKE_WIDTH}
                                      />
                                      <span className={ACTION_TOOLTIP_CLASSES}>
                                        Resolve All Errors
                                      </span>
                                    </button>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-4 py-2.5 text-center">
                              <StatusBadge
                                status_id={caseItem.status_id}
                                canChangeStatus={canChangeStatus()}
                                onStatusChange={(newStatus) =>
                                  handleStatusChange(caseItem.case_id, newStatus)
                                }
                              />
                            </td>
                            {showStaffColumns && (
                              <>
                                <td className="p-0 text-left text-sm text-muted-foreground">
                                  <Link
                                    {...caseRowCellLinkProps(caseItem)}
                                    className={`${CASE_ROW_LINK_CLASSES} truncate px-4 py-2.5`}
                                    title={caseItem.physician || undefined}
                                  >
                                    {caseItem.physician || (
                                      <span className="text-text-disabled">&mdash;</span>
                                    )}
                                  </Link>
                                </td>
                                <td className="whitespace-nowrap px-4 py-2.5">
                                  <CaseAnalystsCell
                                    caseId={caseItem.case_id}
                                    analysts={caseItem.analysts || []}
                                    availableAnalysts={analysts}
                                    isEditable={isAdmin(firmContext)}
                                  />
                                </td>
                                <td className="whitespace-nowrap px-4 py-2.5">
                                  <QcReviewerBadges
                                    caseId={caseItem.case_id}
                                    qcReviewers={caseItem.qc_reviewers || []}
                                    caseAnalysts={caseItem.analysts || []}
                                    availableQcReviewers={analysts}
                                    isEditable={canAssignQC(firmContext)}
                                  />
                                </td>
                              </>
                            )}
                            <td className="whitespace-nowrap p-0 text-center text-sm tabular-nums text-muted-foreground">
                              <Link
                                {...caseRowCellLinkProps(caseItem)}
                                className={`${CASE_ROW_LINK_CLASSES} px-4 py-2.5`}
                              >
                                {caseItem.exam_date ? (
                                  formatDate(caseItem.exam_date)
                                ) : (
                                  <span className="text-text-disabled">&mdash;</span>
                                )}
                              </Link>
                            </td>
                            <td className="whitespace-nowrap p-0 text-center text-sm tabular-nums text-muted-foreground">
                              <Link
                                {...caseRowCellLinkProps(caseItem)}
                                className={`${CASE_ROW_LINK_CLASSES} px-4 py-2.5`}
                              >
                                {caseItem.due_date ? (
                                  formatDate(caseItem.due_date)
                                ) : (
                                  <span className="text-text-disabled">&mdash;</span>
                                )}
                              </Link>
                            </td>
                            <td className="whitespace-nowrap p-0 text-center text-sm tabular-nums text-muted-foreground">
                              <Link
                                {...caseRowCellLinkProps(caseItem)}
                                className={`${CASE_ROW_LINK_CLASSES} px-4 py-2.5`}
                              >
                                {caseItem.files || (
                                  <span className="text-text-disabled">&mdash;</span>
                                )}
                              </Link>
                            </td>
                            <td className="whitespace-nowrap p-0 text-center text-sm tabular-nums text-muted-foreground">
                              <Link
                                {...caseRowCellLinkProps(caseItem)}
                                className={`${CASE_ROW_LINK_CLASSES} px-4 py-2.5`}
                              >
                                {caseItem.pages || (
                                  <span className="text-text-disabled">&mdash;</span>
                                )}
                              </Link>
                            </td>
                            <td className="whitespace-nowrap p-0 text-center text-sm tabular-nums text-muted-foreground">
                              <Link
                                {...caseRowCellLinkProps(caseItem)}
                                className={`${CASE_ROW_LINK_CLASSES} px-4 py-2.5`}
                              >
                                {caseItem.created_at ? (
                                  formatDate(caseItem.created_at)
                                ) : (
                                  <span className="text-text-disabled">&mdash;</span>
                                )}
                              </Link>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Add Error Modal */}
        <Dialog
          open={Boolean(selectedErrors)}
          onOpenChange={(next) => !next && handleCloseErrorModal()}
        >
          <DialogContent size="lg" hideCloseButton className="gap-0 p-0">
            <DialogHeader className="flex-row items-center justify-between space-y-0 p-6 pb-4 text-left">
              <DialogTitle className="text-lg font-semibold text-foreground">
                Case Errors
              </DialogTitle>
              <DialogClose className="text-text-disabled hover:text-muted-foreground">
                <X className="h-5 w-5" />
              </DialogClose>
            </DialogHeader>
            <div className="max-h-[70vh] space-y-6 overflow-y-auto px-6 pb-6">
              {selectedErrors?.map((error, i) => (
                <div
                  key={i}
                  className="rounded-lg border border-destructive-border bg-destructive-subtle p-4"
                >
                  <div className="flex items-start space-x-3">
                    <CircleAlert className="mt-0.5 h-5 w-5 flex-shrink-0 text-destructive-text" />
                    <div className="flex-1">
                      <p className="font-semibold text-destructive-text">{error.description}</p>
                      {error.error_details?.message && (
                        <p className="mt-2 text-destructive-text">{error.error_details.message}</p>
                      )}
                      {error.error_details?.recommended_action && (
                        <div className="mt-3 rounded-lg border border-destructive-border bg-destructive-subtle/50 p-3">
                          <div className="mb-2 flex items-center gap-2">
                            <span role="img" aria-label="lightbulb" className="text-lg">
                              💡
                            </span>
                            <p className="font-bold text-destructive-text">How to fix</p>
                          </div>
                          <p className="text-destructive-text">
                            {error.error_details.recommended_action}
                          </p>
                        </div>
                      )}
                      <p className="mt-2 text-sm text-muted-foreground">
                        {formatDate(error.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default CaseManagement;
